import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import {
  AUTH_DELETE_SESSIONS,
  AUTH_GET_ACTIVE_SESSIONS,
  AUTH_GET_ME,
  AUTH_GET_SESSIONS,
  AUTH_POST_AUTH_TOKEN,
  AUTH_POST_LOGIN,
  AUTH_POST_LOGOUT,
  AUTH_POST_REFRESH,
} from '../endpoints/auth.endpoints';
import { HEADERS } from '../api-headers';
import { toFormBody } from '../api-utils';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { User } from 'src/app/core/models/core.models';

import { map, Observable, catchError, throwError } from 'rxjs';

export type AuthTokenError = { error: true; message: string };
export type AuthTokenSuccess = LoginResponse & { error?: false };
export type AuthTokenResponse = AuthTokenError | AuthTokenSuccess;

export interface LoginCredentials {
  email: string;
  password: string;
  facebook_user_id?: string;
  facebook_access_token?: string;
  remember_me?: boolean;
}

export interface ErrorResponse {
  errors: Error;
  message: string;
  status_code: number;
}

export interface LoginResponse {
  access_token: string;
  access_token_expires_at: string | number;
  user: User;
}

export interface LoginErrorResponse {
  error: true;
  code: string | number;
  message: string;
}

export class LoginApiError extends Error {
  constructor(
    public readonly code: string | number,
    message: string,
    public readonly httpStatus?: number,
  ) {
    super(message);
    this.name = 'LoginApiError';
  }
}

export interface UserSession {
  session_id: number | string;
  user_id: string;
  company_id: string;
  ip_address: string;

  // Mapping the new transformer fields
  browser_raw: string; // The full User Agent string
  browser_brand:
    | 'Chrome'
    | 'Firefox'
    | 'Safari'
    | 'Edge'
    | 'Opera'
    | 'Internet Explorer'
    | 'Other';

  resolution?: string;
  browser_icon?: IconProp;
  is_current: boolean;
  is_impersonating: boolean;

  last_active: number; // Timestamp (seconds)
  started_at: number; // Timestamp (seconds)
  expires_at?: number; // Timestamp (seconds)

  user?: {
    name: string;
    email: string;
    avatar_url?: string;
  };

  company?: {
    id?: string;
    name: string;
  };
}

export interface ActiveSessionsResponse {
  sessions?: UserSession[]; // For personal list
  active_sessions?: UserSession[]; // For global list
}

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  private readonly http = inject(HttpClient);

  login(credentials: LoginCredentials): Observable<LoginResponse> {
    const payload = {
      email: credentials.email,
      password: credentials.password,
      browser: navigator.userAgent,
      resolution: `${window.screen.width}x${window.screen.height}`,
      ...(credentials.facebook_user_id
        ? { facebook_user_id: credentials.facebook_user_id }
        : {}),
      ...(credentials.facebook_access_token
        ? { facebook_access_token: credentials.facebook_access_token }
        : {}),
    };

    return this.http.post<LoginResponse>(AUTH_POST_LOGIN, payload).pipe(
      // Optional runtime sanity check
      map((res) => {
        if (!res?.access_token) {
          throw new Error('Invalid login response structure');
        }
        return res;
      }),
      catchError((e: unknown) => {
        if (e instanceof HttpErrorResponse) {
          const err = e.error as Partial<LoginErrorResponse> | string | null;

          // backend error envelope
          if (err && typeof err === 'object' && err.error === true) {
            return throwError(
              () =>
                new LoginApiError(
                  err.code ?? e.status,
                  err.message ?? 'Login failed',
                  e.status,
                ),
            );
          }

          // fallback
          const msg =
            (typeof err === 'string' && err) ||
            (err &&
              typeof err === 'object' &&
              'message' in err &&
              (err as any).message) ||
            e.message ||
            'Login failed';

          return throwError(() => new LoginApiError(e.status, msg, e.status));
        }

        return throwError(() => e);
      }),
    );
  }

  /**
   * Revoke the session server-side (needs the bearer token); the backend also
   * expires the refresh cookie.
   */
  logout(): Observable<unknown> {
    return this.http.post(AUTH_POST_LOGOUT, {}, { withCredentials: true });
  }

  verifyAuthToken(token: string): Observable<LoginResponse> {
    return this.http.get<LoginResponse>(AUTH_GET_ME, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  loginToken(token: string): Observable<AuthTokenResponse> {
    const headers = HEADERS.formUrlEncoded;

    const urlEncodedData = toFormBody({
      token,
      browser: navigator.userAgent,
      resolution: `${window.screen.width}x${window.screen.height}`,
    });

    return this.http.post<AuthTokenResponse>(
      AUTH_POST_AUTH_TOKEN,
      urlEncodedData.toString(),
      { headers },
    );
  }

  refresh(): Observable<{
    access_token: string;
    access_token_expires_at?: string | number;
  }> {
    // Cookie is sent automatically if withCredentials is true and CORS allows it
    return this.http.post<{
      access_token: string;
      access_token_expires_at?: string | number;
    }>(AUTH_POST_REFRESH, {}, { withCredentials: true });
  }

  /**
   * Fetches ALL active sessions in the system (GET /auth/sessions is the
   * global, admin-restricted listing on the backend).
   */
  getActiveSessions(): Observable<UserSession[]> {
    return this.http.get<UserSession[]>(AUTH_GET_SESSIONS);
  }

  /**
   * Fetches ALL active sessions in the system (Admin only).
   */
  getGlobalActiveSessions(): Observable<UserSession[]> {
    return this.http
      .get<ActiveSessionsResponse>(AUTH_GET_ACTIVE_SESSIONS)
      .pipe(map((res) => res.active_sessions || []));
  }

  /**
   * Revokes a specific session by ID.
   * Works for both personal sessions and admin-forced terminations.
   */
  revokeSession(sessionId: string | number): Observable<any> {
    // We construct the body as a URL-encoded string so PHP's parse_str can read it
    const body = toFormBody({ session_id: sessionId }).toString();

    const headers = HEADERS.formUrlEncodedNoCharset;

    return this.http.delete(AUTH_DELETE_SESSIONS, {
      headers: headers,
      body: body,
    });
  }
}
